// packages/kern/src/ferien.ts — Schulferien (SPEC §3.8).
//
// KEIN DOM: außer `text-canvas.ts` darf keine Datei in `packages/kern/src`
// `document`, `window`, `navigator`, `fetch`, `localStorage` oder einen
// React-Import enthalten.
//
// Der Dekoder liegt im Kern, die DATEN kommen vom Backend (T38).
// `FERIEN_NAMES` und `FERIEN_DATA` liegen NICHT im Bundle; die byteechte Kopie
// in `docs/quellen/understand/ferien-data.txt` (sha256 e6821556…) dient dem
// Backend-Seed und den Tests als Vorlage. NIEMALS abtippen — ein falsches
// Base36-Zeichen verschiebt Ferien lautlos um Tage.
//
// Rumpf wortgleich aus `docs/quellen/understand/prototype.json`,
// reusable_snippets[11] (`ferienSegs`), mit den Bindungen aus §3.8.

import { TB } from "./texte";
import type { Mi, Plan } from "./types";
import { dateToMi, daysInMonth, maxMi, minMi } from "./zeit";

/** Ein Ferienpaket, wie GET /ferien es liefert. */
export interface FerienQuelle {
  bundesland: string;
  /** Base36: "<startTage>.<len>.<nameIndex>", Tokens durch Leerzeichen getrennt. */
  daten: string;
  /** Namensliste; `nameIndex` zeigt hinein. */
  namen: string[];
  /** Epoche als "YYYY-MM-DD", derzeit immer "2023-01-01". */
  epoche: string;
}

export interface FerienSegment { a: Mi; b: Mi; names: string[] }

/**
 * Die Ferienbalken für genau diesen Plan.
 *
 * Ohne Quelle oder bei einem anderen Bundesland kommt eine LEERE Liste — der
 * Ferienrahmen wird trotzdem gezeichnet (§3.9), sonst springt das Blatt in der
 * Höhe, sobald die Ferien nachgeladen sind.
 */
export function ferienSegs(plan: Plan, quelle: FerienQuelle | null): FerienSegment[] {
  const raw = quelle && quelle.bundesland === plan.bl ? quelle.daten : null;
  if (!raw || !quelle) return [];

  // Entspricht Date.UTC(2023,0,1) für die heutige Epoche. Eine unlesbare
  // Epoche ergibt NaN — dann lieber keine Ferien als ein Blatt voller
  // `x="NaN"` (der Prototyp hatte hier eine Konstante und brauchte die Probe
  // nicht; die Daten kommen jetzt über HTTP).
  const epoche = Date.parse(quelle.epoche + "T00:00:00Z");
  if (!Number.isFinite(epoche)) return [];

  const lo = minMi(plan), hi = maxMi(plan);
  const segs: Array<{ a: Mi; b: Mi; name: string }> = [];

  raw.split(" ").forEach((tok) => {
    const p = tok.split(".");
    if (p.length < 3) return;
    const st = parseInt(p[0]!, 36), len = parseInt(p[1]!, 36), ni = parseInt(p[2]!, 36);
    // Unlesbare Zahlen verwerfen, aus demselben Grund wie oben. `ni` darf NaN
    // sein: dann greift der Namensrückfall.
    if (!Number.isFinite(st) || !Number.isFinite(len)) return;
    // `len` ist die einschließende Dauer MINUS 1 — `len+1` sind die Tage.
    if (len + 1 < Math.max(1, plan.ferienMin)) return;
    const a = dateToMi(epoche + st * 864e5);
    const e = dateToMi(epoche + (st + len) * 864e5);
    // `y` ist hier ein MONATSINDEX, kein Jahr. Nicht „korrigieren" (§3.8, R-21).
    const y = Math.floor(e);
    const b = e + 1 / daysInMonth(Math.floor(y / 12), y % 12);
    if (b <= lo || a >= hi) return;
    segs.push({ a: Math.max(a, lo), b: Math.min(b, hi), name: quelle.namen[ni] ?? TB.ferienFallback });
  });

  segs.sort((x, z) => x.a - z.a);

  const out: FerienSegment[] = [];
  segs.forEach((s) => {
    const last = out[out.length - 1];
    if (last && s.a <= last.b + 0.005) {
      last.b = Math.max(last.b, s.b);
      if (last.names.indexOf(s.name) < 0) last.names.push(s.name);
    } else {
      out.push({ a: s.a, b: s.b, names: [s.name] });
    }
  });
  return out;
}
